// Slow -> fast colour scale used by the matrix heatmap.
//
// Matches the reference graphic: a blue -> green -> red gradient where blue is
// "slow" and red is "fast". For throughput metrics a higher value is faster;
// for `ttft_ms` a *lower* value is faster, so that metric is inverted.
import type { ColorMetric } from '../api';

type RGB = [number, number, number];

const STOPS: Array<{ t: number; rgb: RGB }> = [
  { t: 0.0, rgb: [45, 91, 219] }, // blue  (slow)
  { t: 0.5, rgb: [55, 178, 77] }, // green (medium)
  { t: 1.0, rgb: [224, 49, 49] }, // red   (fast)
];

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

/** Map a normalised position in [0,1] to an `rgb()` string. */
export function scaleColor(t: number): string {
  const clamped = Math.max(0, Math.min(1, t));
  for (let i = 0; i < STOPS.length - 1; i++) {
    const lo = STOPS[i];
    const hi = STOPS[i + 1];
    if (clamped <= hi.t) {
      const local = (clamped - lo.t) / (hi.t - lo.t);
      return `rgb(${lerp(lo.rgb[0], hi.rgb[0], local)}, ${lerp(lo.rgb[1], hi.rgb[1], local)}, ${lerp(
        lo.rgb[2],
        hi.rgb[2],
        local,
      )})`;
    }
  }
  return `rgb(${STOPS[STOPS.length - 1].rgb.join(', ')})`;
}

export function normalize(
  value: number,
  min: number,
  max: number,
  metric: ColorMetric,
): number {
  if (max === min) return 0.5;
  const t = (value - min) / (max - min);
  // Lower time-to-first-token is "faster".
  return metric === 'ttft_ms' ? 1 - t : t;
}

export function cellColor(
  value: number | null,
  range: { min: number | null; max: number | null },
  metric: ColorMetric,
): string {
  if (value == null || range.min == null || range.max == null) return 'var(--cell-empty)';
  return scaleColor(normalize(value, range.min, range.max, metric));
}

export const METRIC_LABEL: Record<ColorMetric, string> = {
  generate_tps: 'Generation (tok/s)',
  think_tps: 'Thinking (tok/s)',
  prefill_tps: 'Prefill (tok/s)',
  ttft_ms: 'TTFT (ms)',
};

export function formatMetric(value: number | null | undefined, metric: ColorMetric): string {
  if (value == null || !Number.isFinite(value)) return '–';
  return metric === 'ttft_ms' ? `${Math.round(value)} ms` : `${value.toFixed(1)}`;
}
